import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Toolbar,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  Paper,
  Chip,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Add, Refresh, Edit } from "@mui/icons-material";

import HeaderBar from "../../components/HeaderBar";
import LeftDrawer from "../../components/LeftDrawer";
import AddNoticeMasterModal from "../../components/modals/AddNoticeMasterModal";
import { TableSkeleton } from "../../components/common/SkeletonLoader";
import { useToast } from "../../components/common/ToastProvider";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import EnhancedTable from "../../components/common/EnhancedTable";
import {
  createUpdateNoticeMaster,
  fetchNoticeMaster,
} from "../../redux/masters/noticeSlice";
import { OptionsStatus } from "../../constants/enum";
import { delay } from "framer-motion";

const NoticeMaster = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    items: noticesMaster,
    loading,
    error,
  } = useSelector((state) => state.noticesMaster);

  const { showSuccess, showError } = useToast();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editingLoanId, setEditingLoanId] = useState(null); // for edit
  const [addLoading, setAddLoading] = useState(false); // for add

  useEffect(() => {
    dispatch(fetchNoticeMaster());
  }, [dispatch]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleDrawerClose = () => setMobileOpen(false);
  const handleCloseModal = () => setOpenModal(false);

  const handleAddNew = async () => {
    try {
      setAddLoading(true);
      setEditData(null);
      setOpenModal(true);
    } catch (err) {
      showError("Failed to load notice options");
    } finally {
      setAddLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      await dispatch(fetchNoticeMaster()).unwrap();
      showSuccess("Data refreshed successfully");
    } catch (error) {
      showError(error.message || "Failed to refresh data");
    }
  };

  const handleSaveNoticeMaster = async (form) => {
    try {
      await dispatch(createUpdateNoticeMaster(form)).unwrap();
      await dispatch(fetchNoticeMaster()).unwrap();
      showSuccess("Notice created successfully");
      handleCloseModal();
    } catch (error) {
      showError(error.message || "Failed to notice");
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <HeaderBar onMobileMenuToggle={handleDrawerToggle} />
      <LeftDrawer
        activeController="/masters/notice"
        mobileOpen={mobileOpen}
        onMobileClose={handleDrawerClose}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "background.default",
          p: { xs: 1, sm: 2, md: 3 },
          minHeight: "100vh",
          width: { sm: `calc(100% - ${isMobile ? 0 : 240}px)` },
        }}
      >
        <AddNoticeMasterModal
          open={openModal}
          onClose={() => {
            setOpenModal(false);
            setEditData(null);
          }}
          onSave={handleSaveNoticeMaster}
          data={editData} // pass selected item to modal
        />

        <Toolbar sx={{ minHeight: 64 }} />
        <Breadcrumbs />

        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardHeader
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              backgroundColor: "background.secondary",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
            title={
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.125rem" },
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Notice
              </Typography>
            }
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Refresh Data">
                  <IconButton
                    onClick={handleRefresh}
                    size="small"
                    sx={{ display: { xs: "inline-flex", sm: "none" } }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>

                <Button
                  variant="contained"
                  size={isMobile ? "small" : "medium"}
                  onClick={handleAddNew}
                  startIcon={
                    addLoading ? <CircularProgress size={16} /> : <Add />
                  }
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                  disabled={addLoading}
                >
                  {isMobile ? "Add" : "Add New"}
                </Button>

                {!isMobile && (
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={handleRefresh}
                    startIcon={<Refresh />}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 500,
                    }}
                  >
                    Refresh
                  </Button>
                )}
              </Box>
            }
          />
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            {loading ? (
              <TableSkeleton rows={5} columns={13} />
            ) : error ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            ) : (
              <EnhancedTable
                data={Array.isArray(noticesMaster) ? noticesMaster : []}
                columns={[
                  {
                    id: "noticeType",
                    label: "Notice Type",
                    sortable: true,
                    render: (value) => value,
                  },
                  {
                    id: "charge",
                    label: "Charge",
                    sortable: true,
                    render: (value) => (
                      <Typography sx={{ fontWeight: 500 }}>{value}</Typography>
                    ),
                  },
                  {
                    id: "suretyCharge",
                    label: "Surety Charge (%)",
                    sortable: true,
                    render: (value) => `₹${value}`,
                    hidden: isMobile,
                  },
                  {
                    id: "remarks",
                    label: "Remarks",
                    sortable: true,
                    hidden: isMobile,
                  },
                  {
                    id: "active",
                    label: "Status",
                    sortable: true,
                    render: (value) =>
                      value ? (
                        <Chip
                          label="Active"
                          size="small"
                          color="success"
                        />
                      ) : (
                        <Chip
                          label="Inactive"
                          size="small"
                          variant="outlined"
                        />
                      ),
                  },
                  {
                    id: "editable",
                    label: "Editable",
                    sortable: false,
                    render: (_, row) => (
                      <Tooltip
                        title={
                          row.editable
                            ? "Edit notice Type"
                            : "Data is not editable"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={
                              !row.editable || editingLoanId !== null
                            }
                            onClick={async (e) => {
                              e.stopPropagation();
                              setEditingLoanId(row.id);
                              setEditData(row);
                              setOpenModal(true);
                              setEditingLoanId(null);
                            }}
                          >
                            {editingLoanId === row.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Edit fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ),
                  },
                ]}
                title="Notices"
                loading={loading}
                emptyMessage="No notices found"
                stickyHeader={true}
                dense={true}
                showRowNumbers={true}
              />
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default NoticeMaster;
